import type { BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import {
  MARKER_DIAMETER,
  markerDiscAxisOffsetY,
  timelineRailAxisY,
} from "../../marker/MarkerMetrics/metrics";
import { markerRailPose } from "../../rail/RailPose/pose";
import { computeSceneMotion } from "@/lib/timeline/scene/SceneMotion/motion";
import { boardViewportXDelta } from "../BoardSample/viewportDelta";
import { sampleBoard } from "../BoardSample/sample";
import type { FitCastBoardContext } from "./context";

export interface CharacterCenter {
  x: number;
  y: number;
}

/**
 * Pan X stays on the playhead so seat eases do not slow the locked camera.
 * Y is the midpoint of the visible discs.
 */
export function visibleCharacterCenter(
  layout: BookTimelineLayout,
  viewportWidth: number,
  playheadMs: number,
  boardContext?: FitCastBoardContext | null,
): CharacterCenter | null {
  const points = boardContext
    ? centersFromBoard(boardContext, viewportWidth, playheadMs)
    : centersFromLayout(layout, viewportWidth, playheadMs);
  const span = spanCenter(points);
  if (span === null) {
    return null;
  }
  const motion = computeSceneMotion(
    playheadMs,
    viewportWidth,
    layout.totalDurationMs,
  );
  return { x: motion.groupCenterX, y: span.y };
}

function centersFromBoard(
  boardContext: FitCastBoardContext,
  viewportWidth: number,
  playheadMs: number,
): CharacterCenter[] {
  const sample = boardContext.sample.playheadMs === playheadMs
    ? boardContext.sample
    : sampleBoard(boardContext.board, playheadMs);
  const deltaX = boardViewportXDelta(
    viewportWidth,
    boardContext.board.referenceViewportWidth,
  );
  const half = MARKER_DIAMETER / 2;
  const axis = markerDiscAxisOffsetY();
  const points: CharacterCenter[] = [];
  for (const entry of sample.characters) {
    if (!entry.pose.visible || entry.parked === true) {
      continue;
    }
    points.push({
      x: entry.pose.x + deltaX,
      y: entry.pose.y + half + axis,
    });
  }
  return points;
}

function centersFromLayout(
  layout: BookTimelineLayout,
  viewportWidth: number,
  playheadMs: number,
): CharacterCenter[] {
  const motion = computeSceneMotion(
    playheadMs,
    viewportWidth,
    layout.totalDurationMs,
  );
  const railY = timelineRailAxisY();
  const points: CharacterCenter[] = [];
  for (const marker of layout.markers) {
    const pose = markerRailPose(marker.characterId, playheadMs, layout.rail);
    if (pose === null || pose.parked === true) {
      continue;
    }
    points.push({
      x: motion.groupCenterX + pose.dx,
      y: railY + pose.railDy + pose.dy,
    });
  }
  return points;
}

function spanCenter(points: readonly CharacterCenter[]): CharacterCenter | null {
  const first = points[0];
  if (first === undefined) {
    return null;
  }
  let minX = first.x;
  let maxX = first.x;
  let minY = first.y;
  let maxY = first.y;
  for (const point of points) {
    minX = Math.min(minX, point.x);
    maxX = Math.max(maxX, point.x);
    minY = Math.min(minY, point.y);
    maxY = Math.max(maxY, point.y);
  }
  return { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
}

/** Zoom that keeps every edge of the bounds on screen around a fixed center. */
export function zoomToFitAroundCenter(
  bounds: { minX: number; minY: number; maxX: number; maxY: number },
  center: CharacterCenter,
  stageW: number,
  stageH: number,
  padPx: number,
): number {
  const halfW = Math.max(center.x - bounds.minX, bounds.maxX - center.x, 1);
  const halfH = Math.max(center.y - bounds.minY, bounds.maxY - center.y, 1);
  const pad = Math.min(padPx, stageW * 0.06, stageH * 0.06);
  const spanX = Math.max(1, stageW / 2 - pad);
  const spanY = Math.max(1, stageH / 2 - pad);
  return Math.min(spanX / halfW, spanY / halfH);
}
